import React, { useMemo } from 'react';
import { getScene } from '../../config/membership';
import ImagePaintingCanvas from './ImagePaintingCanvas';

export default function PaintingCanvas({ id, scene, progress, artSeed, className, isRunning = false }) {
  const catalogScene = getScene(scene);

  if (catalogScene?.type === 'image') {
    return (
      <ImagePaintingCanvas
        id={id}
        scene={catalogScene}
        progress={progress}
        artSeed={artSeed}
        className={className}
        isRunning={isRunning}
      />
    );
  }

  // Simple deterministic PRNG
  const strokes = useMemo(() => {
    let seed = artSeed || 12345;
    const prng = () => {
      let x = Math.sin(seed++) * 10000;
      return x - Math.floor(x);
    };

    const generated = [];
    const addStroke = (d, fill, stroke, strokeWidth, opacity, isHighlight = false) => {
      generated.push({ d, fill, stroke, strokeWidth, opacity, isHighlight, id: `s_${generated.length}` });
    };

    if (scene === 'dawn') {
      // Dawn Garden
      for (let i = 0; i < 100; i++) {
        let y = prng() * 150;
        let w = 100 + prng() * 200;
        let x = prng() * 400 - 50;
        addStroke(`M${x},${y} Q${x+w/2},${y+10} ${x+w},${y}`, "none", "#E8AC7F", 10 + prng()*20, 0.1 + prng()*0.1);
      }
      for (let i = 0; i < 150; i++) {
        let angle = prng() * Math.PI * 2;
        let r = prng() * 60;
        let cx = 200 + Math.cos(angle)*r;
        let cy = 150 + Math.sin(angle)*r;
        addStroke(`M${cx-10},${cy} L${cx+10},${cy}`, "none", "#E2A93B", 5 + prng()*10, 0.2 + prng()*0.2);
      }
      for (let i = 0; i < 100; i++) {
        let x = prng() * 400;
        let y = 180 + prng() * 120;
        let w = 50 + prng() * 100;
        addStroke(`M${x},${y} Q${x+w/2},${y-20} ${x+w},${y+10}`, "none", "#A9C4A5", 10 + prng()*20, 0.3 + prng()*0.2);
      }
      for (let i = 0; i < 100; i++) {
        let x = prng() * 400;
        let y = 220 + prng() * 80;
        let w = 50 + prng() * 100;
        addStroke(`M${x},${y} Q${x+w/2},${y-30} ${x+w},${y}`, "none", "#173C32", 10 + prng()*20, 0.4 + prng()*0.2);
      }
      for (let i = 0; i < 100; i++) {
        let x = prng() * 400;
        let h = 20 + prng() * 60;
        let y = 300 - prng() * 40;
        addStroke(`M${x},${y} Q${x+10},${y-h/2} ${x-5},${y-h}`, "none", "#173C32", 2 + prng()*4, 0.6 + prng()*0.3);
        addStroke(`M${x-10},${y-h} Q${x},${y-h-10} ${x+10},${y-h}`, "none", "#F6F3EC", 4 + prng()*6, 0.7 + prng()*0.3, true);
      }
    } else if (scene === 'coast') {
      // Quiet Coast
      for (let i = 0; i < 150; i++) {
        let y = prng() * 150;
        let w = 150 + prng() * 200;
        let x = prng() * 400 - 50;
        addStroke(`M${x},${y} Q${x+w/2},${y+10} ${x+w},${y-10}`, "none", "#C5BADE", 10 + prng()*20, 0.15 + prng()*0.15);
      }
      for (let i = 0; i < 250; i++) {
        let y = 150 + prng() * 150;
        let w = 100 + prng() * 200;
        let x = prng() * 400 - 50;
        addStroke(`M${x},${y} L${x+w},${y}`, "none", "#2F6B4B", 8 + prng()*15, 0.3 + prng()*0.2);
      }
      for (let i = 0; i < 150; i++) {
        let x = (prng() > 0.5) ? prng() * 100 : 300 + prng() * 100;
        let y = 100 + prng() * 200;
        let h = 50 + prng() * 100;
        addStroke(`M${x},${y} L${x+20*(prng()-0.5)},${y+h}`, "none", "#2C2C2C", 10 + prng()*20, 0.4 + prng()*0.3);
      }
      for (let i = 0; i < 150; i++) {
        let x = prng() * 400;
        let y = 160 + prng() * 120;
        let w = 20 + prng() * 50;
        addStroke(`M${x},${y} Q${x+w/2},${y-5} ${x+w},${y}`, "none", "#F6F3EC", 3 + prng()*5, 0.6 + prng()*0.4, true);
      }
    } else if (scene === 'golden-hour') {
      // Golden Hour
      for (let i = 0; i < 120; i++) {
        let y = prng() * 170;
        let w = 120 + prng() * 220;
        let x = prng() * 400 - 50;
        addStroke(`M${x},${y} Q${x+w/2},${y+8} ${x+w},${y-4}`, "none", i % 3 === 0 ? "#E2A93B" : "#E8AC7F", 12 + prng()*24, 0.12 + prng()*0.18);
      }
      for (let i = 0; i < 150; i++) {
        let x = prng() * 400;
        let y = 165 + prng() * 135;
        let w = 60 + prng() * 150;
        addStroke(`M${x},${y} Q${x+w/2},${y-35} ${x+w},${y-8}`, "none", i % 3 === 0 ? "#E2A93B" : "#8FA36E", 10 + prng()*18, 0.25 + prng()*0.3);
      }
      for (let i = 0; i < 120; i++) {
        let x = prng() * 400;
        let y = 225 + prng() * 75;
        let h = 25 + prng() * 55;
        addStroke(`M${x},${y} Q${x+5},${y-h/2} ${x},${y-h}`, "none", "#496849", 2 + prng()*4, 0.45 + prng()*0.35);
      }
      for (let i = 0; i < 80; i++) {
        let angle = prng() * Math.PI * 2;
        let r = prng() * 30;
        let cx = 310 + Math.cos(angle)*r;
        let cy = 72 + Math.sin(angle)*r;
        addStroke(`M${cx-6},${cy} L${cx+6},${cy}`, "none", "#F6F3EC", 5 + prng()*10, 0.35 + prng()*0.3, true);
      }
    } else if (scene === 'alpine-lake') {
      // Alpine Lake
      for (let i = 0; i < 130; i++) {
        let x = prng() * 400;
        let peak = 45 + prng() * 90;
        let width = 55 + prng() * 95;
        addStroke(`M${x-width/2},${170} L${x},${peak} L${x+width/2},${170}`, "none", "#567981", 8 + prng()*14, 0.18 + prng()*0.25);
      }
      for (let i = 0; i < 70; i++) {
        let x = prng() * 400;
        let peak = 45 + prng() * 75;
        addStroke(`M${x-18},${peak+25} L${x},${peak} L${x+18},${peak+25}`, "none", "#F6F3EC", 4 + prng()*8, 0.45 + prng()*0.35, true);
      }
      for (let i = 0; i < 220; i++) {
        let y = 165 + prng() * 135;
        let w = 45 + prng() * 130;
        let x = prng() * 400 - 30;
        addStroke(`M${x},${y} Q${x+w/2},${y+3} ${x+w},${y}`, "none", i % 5 === 0 ? "#F6F3EC" : "#A8CAD0", 3 + prng()*10, 0.2 + prng()*0.38);
      }
      for (let i = 0; i < 80; i++) {
        let x = prng() * 400;
        let y = 210 + prng() * 90;
        addStroke(`M${x},${y} L${x+12*(prng()-0.5)},${y-35-prng()*45}`, "none", "#466052", 4 + prng()*8, 0.45 + prng()*0.35);
      }
    } else if (scene === 'sakura-garden') {
      // Sakura Garden
      for (let i = 0; i < 80; i++) {
        let fromLeft = prng() > 0.5;
        let x = fromLeft ? prng() * 120 : 280 + prng() * 120;
        let y = prng() * 190;
        let targetX = 200 + (prng()-0.5) * 100;
        let targetY = 100 + prng() * 100;
        addStroke(`M${x},${y} Q${(x+targetX)/2},${targetY-25} ${targetX},${targetY}`, "none", "#705348", 4 + prng()*10, 0.35 + prng()*0.42);
      }
      for (let i = 0; i < 220; i++) {
        let side = prng() > 0.5;
        let cx = side ? 20 + prng() * 190 : 210 + prng() * 180;
        let cy = 15 + prng() * 170;
        let radius = 2 + prng() * 6;
        addStroke(`M${cx-radius},${cy} Q${cx},${cy-radius} ${cx+radius},${cy}`, "none", i % 4 === 0 ? "#F6F3EC" : "#E9B7B0", 3 + prng()*7, 0.4 + prng()*0.45, true);
      }
      for (let i = 0; i < 120; i++) {
        let x = prng() * 400;
        let y = 200 + prng() * 100;
        let w = 35 + prng() * 100;
        addStroke(`M${x},${y} Q${x+w/2},${y-25} ${x+w},${y}`, "none", "#8FA98B", 8 + prng()*14, 0.28 + prng()*0.3);
      }
      for (let i = 0; i < 80; i++) {
        let y = 180 + prng() * 120;
        let spread = (y - 180) * 0.35;
        addStroke(`M${200-spread},${y} L${200+spread},${y}`, "none", "#F8EFEA", 5 + prng()*10, 0.35 + prng()*0.4);
      }
    } else {
      // Moonlit Meadow
      for (let i = 0; i < 250; i++) {
        let y = prng() * 200;
        let w = 100 + prng() * 200;
        let x = prng() * 400 - 50;
        addStroke(`M${x},${y} L${x+w},${y+5}`, "none", "#173C32", 15 + prng()*25, 0.2 + prng()*0.2);
      }
      for (let i = 0; i < 150; i++) {
        let angle = prng() * Math.PI * 2;
        let r = prng() * 40;
        let cx = 300 + Math.cos(angle)*r;
        let cy = 100 + Math.sin(angle)*r;
        addStroke(`M${cx-5},${cy} L${cx+5},${cy}`, "none", "#F6F3EC", 5 + prng()*10, 0.3 + prng()*0.2);
      }
      for (let i = 0; i < 150; i++) {
        let x = prng() * 400;
        let y = 200 + prng() * 100;
        let w = 50 + prng() * 150;
        addStroke(`M${x},${y} Q${x+w/2},${y-30} ${x+w},${y}`, "none", "#2C2C2C", 15 + prng()*25, 0.4 + prng()*0.3);
      }
      for (let i = 0; i < 200; i++) {
        let x = prng() * 400;
        let y = 220 + prng() * 80;
        if (prng() > 0.7) {
          addStroke(`M${x-2},${y-30} L${x+2},${y-30}`, "none", "#E2A93B", 4 + prng()*4, 0.8 + prng()*0.2, true);
        } else {
          addStroke(`M${x},${y+20} Q${x+10},${y} ${x},${y-20}`, "none", "#C5BADE", 2 + prng()*4, 0.4 + prng()*0.3);
        }
      }
    }
    return generated;
  }, [scene, artSeed]);

  const p = Math.max(0, Math.min(1, progress));
  const totalStrokes = strokes.length;
  const visibleCount = Math.floor(p * totalStrokes);
  const partialFraction = (p * totalStrokes) - visibleCount;

  return (
    <div className={`relative bg-paper overflow-hidden ${className}`}>
      <svg id={id} viewBox="0 0 400 300" className="w-full h-full" preserveAspectRatio="xMidYMid slice" xmlns="http://www.w3.org/2000/svg">
        <rect width="400" height="300" fill="#F6F3EC" />
        {strokes.slice(0, visibleCount + 1).map((stroke, i) => {
          const isPartial = i === visibleCount;
          if (isPartial && partialFraction === 0) return null;
          
          return (
            <path 
              key={stroke.id}
              d={stroke.d}
              fill={stroke.fill}
              stroke={stroke.stroke}
              strokeWidth={stroke.strokeWidth}
              strokeLinecap="round"
              strokeLinejoin="round"
              opacity={stroke.opacity}
              pathLength={isPartial ? "1" : undefined}
              strokeDasharray={isPartial ? "1" : undefined}
              strokeDashoffset={isPartial ? (1 - partialFraction) : undefined}
            />
          );
        })}
      </svg>
      
      {/* Texture Overlay */}
      <div 
        className="absolute inset-0 opacity-[0.03] mix-blend-multiply pointer-events-none"
        style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")` }}
      />
    </div>
  );
}